class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.rdRefreshRequestId = 0;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    /*
     * The overlay lives outside .drawer__inner in the Durvient drawer,
     * so it survives every cart-content refresh.
     */
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => this.close());

    document.addEventListener('click', (event) => {
      const cartLink = event.target instanceof Element ? event.target.closest('#cart-icon-bubble') : null;
      if (!cartLink) return;
      if (event.rdVardaCartHandled) return;
      event.preventDefault();
      this.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target instanceof Element ? event.target.closest('#cart-icon-bubble') : null;
      if (!cartLink) return;
      if (event.code.toUpperCase() !== 'SPACE') return;
      event.preventDefault();
      this.open(cartLink);
    });

    this.setHeaderCartIconAccessibility();
    this.syncHeaderCount(this.dataset.rdCartCount);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', this.classList.contains('active') ? 'true' : 'false');
  }

  syncHeaderCount(value) {
    const count = Math.max(0, Number.parseInt(value, 10) || 0);
    this.dataset.rdCartCount = String(count);

    document.querySelectorAll('[data-rd-header-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.classList.toggle('is-empty', count === 0);
      node.hidden = false;
    });

    /* Keep any native Dawn bubble in sync without replacing the custom header icon. */
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (bubble) {
      const visibleCount = bubble.querySelector('[aria-hidden="true"]');
      if (visibleCount) visibleCount.textContent = count < 100 ? String(count) : '';
      bubble.hidden = count === 0;
    }

    const drawerCount = this.querySelector('.rd-cart-drawer__count');
    if (drawerCount) {
      drawerCount.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const inner = this.querySelector('.drawer__inner');
    if (!inner) return false;

    inner.removeAttribute('inert');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');

    /*
     * Open state is applied BEFORE focus management. On iOS Safari a focus
     * exception must never prevent the drawer from becoming visible.
     */
    const focusElement = this.querySelector('.drawer__close') || inner;
    const focusDrawer = () => {
      try {
        if (typeof trapFocus === 'function') {
          trapFocus(inner, focusElement);
        } else if (typeof focusElement?.focus === 'function') {
          try {
            focusElement.focus({ preventScroll: true });
          } catch (_) {
            focusElement.focus();
          }
        }
      } catch (error) {
        console.warn('[RD Cart Drawer] Focus management failed after open', error);
      }
    };

    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focusDrawer);
    else focusDrawer();

    try {
      this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
    } catch (_) {}

    return true;
  }

  close() {
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.querySelector('.drawer__inner')?.setAttribute('inert', '');

    try {
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      else this.activeElement?.focus?.();
    } catch (_) {}

    document.body.classList.remove('overflow-hidden');
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
  }

  /**
   * Authoritative Durvient cart refresh.
   *
   * product-form.js, cart.js, recommended-product quick add and other cart
   * paths already call refreshFromCart(). The method was missing from the
   * active drawer controller, which is why add-to-cart could succeed while
   * the iPhone drawer stayed closed/stale.
   */
  async refreshFromCart({ open = false } = {}) {
    const requestId = ++this.rdRefreshRequestId;
    const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
    const separator = cartUrl.includes('?') ? '&' : '?';
    const url = `${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Cart drawer refresh failed (${response.status})`);
      }

      const sections = await response.json();

      /* Never allow an older, slower refresh to overwrite newer cart HTML. */
      if (requestId !== this.rdRefreshRequestId) return true;

      const applied = this.renderContents({ sections }, false);
      if (!applied) throw new Error('Fresh cart drawer HTML could not be applied');

      if (open) this.open(this.activeElement);
      return true;
    } catch (error) {
      console.warn('[RD Cart Drawer] refreshFromCart failed', error);
      if (open && requestId === this.rdRefreshRequestId) this.open(this.activeElement);
      return false;
    }
  }

  renderContents(parsedState, shouldOpen = true) {
    const drawerSectionHTML = parsedState?.sections?.['cart-drawer'];
    const cartIconSectionHTML = parsedState?.sections?.['cart-icon-bubble'];

    let applied = false;

    if (drawerSectionHTML) {
      applied = this.applyDrawerSectionHTML(drawerSectionHTML) || applied;
    }

    if (cartIconSectionHTML) {
      this.applyCartIconSectionHTML(cartIconSectionHTML);
    }

    this.setHeaderCartIconAccessibility();

    if (shouldOpen) this.open(this.activeElement);
    return applied;
  }

  applyDrawerSectionHTML(html) {
    if (!html) return false;

    const documentHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceDrawer = documentHTML.querySelector('cart-drawer');
    const sourceInner = sourceDrawer?.querySelector('.drawer__inner');
    const targetInner = this.querySelector('.drawer__inner');

    if (!sourceDrawer || !sourceInner || !targetInner) return false;

    const wasOpen = this.classList.contains('active');

    /*
     * Durvient does NOT have CartDrawer-Header/Body/Footer wrapper IDs.
     * Refresh the real contract instead: the complete .drawer__inner.
     * The outer <cart-drawer>, overlay and constructor listeners stay mounted.
     */
    targetInner.className = sourceInner.className;

    for (const attr of [...targetInner.attributes]) {
      if (attr.name === 'class') continue;
      if (!sourceInner.hasAttribute(attr.name)) targetInner.removeAttribute(attr.name);
    }
    for (const attr of [...sourceInner.attributes]) {
      if (attr.name === 'class') continue;
      targetInner.setAttribute(attr.name, attr.value);
    }

    targetInner.innerHTML = sourceInner.innerHTML;

    const isEmpty = sourceDrawer.classList.contains('is-empty');
    this.classList.toggle('is-empty', isEmpty);

    if (sourceDrawer.dataset.rdCartCount !== undefined) {
      this.syncHeaderCount(sourceDrawer.dataset.rdCartCount);
    }

    if (sourceDrawer.dataset.rdCurrency) {
      this.dataset.rdCurrency = sourceDrawer.dataset.rdCurrency;
    }

    if (wasOpen) {
      targetInner.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
    }

    document.dispatchEvent(
      new CustomEvent('rd:cart-drawer:rendered', {
        detail: { drawer: this, isEmpty },
      })
    );

    window.RDDurvientCartEnhancements?.refresh?.();
    return true;
  }

  applyCartIconSectionHTML(html) {
    if (!html) return false;

    /*
     * The Durvient header uses its own Font Awesome bag icon and
     * [data-rd-header-cart-count]. Replacing #cart-icon-bubble.innerHTML with
     * Dawn's native icon markup destroys that header. Read the count only.
     */
    const documentHTML = new DOMParser().parseFromString(html, 'text/html');
    const bubble = documentHTML.querySelector('.cart-count-bubble');
    const countNode = bubble?.querySelector('[aria-hidden="true"]');

    if (countNode) {
      this.syncHeaderCount(Number.parseInt(countNode.textContent, 10) || 0);
    } else if (!bubble) {
      this.syncHeaderCount(0);
    }

    return true;
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer' }, { id: 'cart-icon-bubble' }];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.rdMutationQueue = [];
    this.rdMutationRunning = false;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  onCartUpdate(event) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();

    if (event?.cartData?.sections?.['cart-drawer']) {
      drawer.renderContents(event.cartData, false);
      return Promise.resolve(true);
    }

    if (typeof drawer.refreshFromCart === 'function') {
      return drawer.refreshFromCart({ open: drawer.classList.contains('active') });
    }

    return Promise.resolve(false);
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    const quantityInput = row?.querySelector('[data-quantity-line-key]');
    const lineKey = quantityInput?.dataset.quantityLineKey || row?.dataset.cartItemKey || '';
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const action = quantity === 0 ? 'remove' : 'update';
    const deferred = this.createCartLinesUpdateEvent(action, lineVariantId, quantity, lineKey);
    const eventTarget = quantity === 0 ? 'clear' : 'change';

    const existing = this.rdMutationQueue.find((job) => job.queueKey === lineKey);
    if (existing) {
      existing.quantity = quantity;
      existing.line = Number(line);
      existing.name = name;
      existing.variantId = lineVariantId;
      existing.eventTarget = eventTarget;
      if (deferred) existing.deferreds.push(deferred);
    } else {
      this.rdMutationQueue.push({
        queueKey: lineKey || `line-${line}`,
        line: Number(line),
        quantity,
        name,
        variantId: lineVariantId,
        eventTarget,
        deferreds: deferred ? [deferred] : [],
      });
    }

    this.rdSetBusy(line, true, quantity === 0);
    this.rdProcessMutationQueue();
  }

  async rdProcessMutationQueue() {
    if (this.rdMutationRunning) return;
    this.rdMutationRunning = true;

    while (this.rdMutationQueue.length) {
      const job = this.rdMutationQueue.shift();
      const marker = CartPerformance.createStartingMarker(`${job.eventTarget}:user-action`);

      try {
        const body = JSON.stringify({
          line: job.line,
          quantity: job.quantity,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body });
        const parsedState = await response.json();

        if (!response.ok || parsedState.errors) {
          const message = parsedState.errors || window.cartStrings.error;
          throw Object.assign(new Error(message), { code: response.ok ? 'INVALID' : 'SERVICE_UNAVAILABLE' });
        }

        job.deferreds.forEach((deferred) => this.resolveCartLinesUpdate(deferred, parsedState));

        if (job.quantity === 0) {
          this.rdMutationQueue.forEach((pendingJob) => {
            if (pendingJob.line > job.line) pendingJob.line -= 1;
          });
        }

        if (this.rdMutationQueue.length === 0) {
          const drawer = document.querySelector('cart-drawer');
          drawer?.renderContents(parsedState, false);

          const errorBox = document.getElementById('CartDrawer-CartErrors');
          if (errorBox) errorBox.textContent = '';

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: parsedState,
            variantId: job.variantId,
          });
        }
      } catch (error) {
        job.deferreds.forEach((deferred) => deferred.reject(error));

        if (this.rdMutationQueue.length === 0) {
          await this.onCartUpdate();
        }

        this.rdShowError(error.message || window.cartStrings.error);
      } finally {
        CartPerformance.measureFromMarker(`${job.eventTarget}:user-action`, marker);
      }
    }

    this.rdMutationRunning = false;
    this.rdClearBusyState();
  }

  rdSetBusy(line, busy, removing = false) {
    const items = this.querySelector('#CartDrawer-CartItems');
    if (items) items.setAttribute('aria-busy', busy ? 'true' : 'false');

    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    row?.classList.toggle('is-updating', busy);
    row?.classList.toggle('is-removing', busy && removing);

    row?.querySelectorAll('.loading__spinner').forEach((spinner) => {
      spinner.classList.toggle('hidden', !busy);
    });

    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) {
      checkout.disabled = busy;
      checkout.setAttribute('aria-disabled', busy ? 'true' : 'false');
    }

    this.querySelectorAll('.varda-cart-deal__add:not(:disabled)').forEach((button) => {
      if (!busy) return;
      button.dataset.rdCartSyncLock = 'true';
      button.setAttribute('aria-disabled', 'true');
    });
  }

  rdClearBusyState() {
    const items = this.querySelector('#CartDrawer-CartItems');
    items?.removeAttribute('aria-busy');

    this.querySelectorAll('.varda-cart-item').forEach((row) => {
      row.classList.remove('is-updating', 'is-removing');
      row.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
    });

    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) {
      checkout.disabled = false;
      checkout.removeAttribute('aria-disabled');
    }

    this.querySelectorAll('[data-rd-cart-sync-lock]').forEach((button) => {
      button.removeAttribute('data-rd-cart-sync-lock');
      button.removeAttribute('aria-disabled');
    });
  }

  rdShowError(message) {
    const errorBox = document.getElementById('CartDrawer-CartErrors');
    if (errorBox) errorBox.textContent = message;
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}